/**
 * Tour logic with no React and no network: which windows a form shows, which day-and-window picks a choice makes, the legacy
 * `preferredTimes` built from them, how the visitor page words a day and a time, and how a time in the organization's zone reads in
 * the visitor's. Pure and deterministic, so the form, the page and the tests share it.
 *
 * Dates are organization-local `YYYY-MM-DD` and times are organization-local `HH:mm`, as on the wire. They are never turned into an
 * instant here unless a zone conversion is asked for.
 */
import type { HfTourOffer, HfWindowKey } from "./model";

export const WINDOW_KEYS: readonly HfWindowKey[] = ["morning", "afternoon", "evening"];
export const WINDOW_LABELS: Readonly<Record<HfWindowKey, string>> = { morning: "Morning", afternoon: "Afternoon", evening: "Evening" };

/** The hour each window used to be sent as when a request was a list of instants (`preferredTimes`). Legacy, kept until the API confirms the deploy. */
const LEGACY_HOUR: Readonly<Record<HfWindowKey, number>> = { morning: 10, afternoon: 14, evening: 18 };

export const isWindowKey = (value: unknown): value is HfWindowKey => typeof value === "string" && (WINDOW_KEYS as readonly string[]).includes(value);

/** The visitor's choice in the form: days and windows, crossed. */
export interface TourChoice {
  days: string[];
  slots: HfWindowKey[];
}

/** One day and window; what the API takes as a pick. */
export interface TourPickValue {
  date: string;
  window: HfWindowKey;
}

/**
 * The window chips to show: only windows open on at least one picked day. With no day picked yet, every window the offer has.
 */
export function visibleWindows(offer: HfTourOffer, pickedDays: readonly string[]): HfWindowKey[] {
  const open = new Set<HfWindowKey>();
  const days = pickedDays.length > 0 ? offer.days.filter((day) => pickedDays.includes(day.date)) : offer.days;
  for (const day of days) for (const key of day.windows) open.add(key);
  return WINDOW_KEYS.filter((key) => open.has(key));
}

/** Drops picked days the offer does not have and windows no picked day has open, so the state never holds a choice the chips cannot show. */
export function pruneChoice(offer: HfTourOffer, choice: TourChoice): TourChoice {
  const days = choice.days.filter((date) => offer.days.some((day) => day.date === date));
  const show = visibleWindows(offer, days);
  return { days, slots: choice.slots.filter((slot) => show.includes(slot)) };
}

/**
 * The picks of a choice: every picked day crossed with every picked window, but only where the organization is open (a window
 * closed on one of the days is simply not a pick there). Sorted by day, then morning, afternoon, evening.
 */
export function picksOf(offer: HfTourOffer, choice: TourChoice): TourPickValue[] {
  const out: TourPickValue[] = [];
  for (const day of [...offer.days].sort((a, b) => a.date.localeCompare(b.date))) {
    if (!choice.days.includes(day.date)) continue;
    for (const key of WINDOW_KEYS) if (choice.slots.includes(key) && day.windows.includes(key)) out.push({ date: day.date, window: key });
  }
  return out;
}

/** The legacy `preferredTimes` for picks: each window at its old hour, in the visitor's own time zone, as ISO instants. */
export function preferredTimesOf(picks: readonly TourPickValue[], max = 5): string[] {
  return picks.slice(0, max).map(({ date, window }) => {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d, LEGACY_HOUR[window], 0, 0).toISOString();
  });
}

/* ---------- words for days and times ---------- */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CLOCK = /^(\d{1,2}):(\d{2})$/;

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const at = new Date(Date.UTC(y, m - 1, d, 12));
  return at.getUTCFullYear() === y && at.getUTCMonth() === m - 1 && at.getUTCDate() === d;
}

export function weekdayOf(date: string): string {
  return WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()];
}

/** "Nov 4" */
export function monthDayOf(date: string): string {
  const at = new Date(`${date}T12:00:00Z`);
  return `${MONTHS[at.getUTCMonth()]} ${at.getUTCDate()}`;
}

/** "Wed, Nov 4" */
export const dayLabelOf = (date: string): string => `${weekdayOf(date)}, ${monthDayOf(date)}`;

/** "14:30" -> "2:30 PM"; "09:00" -> "9:00 AM". Null when it is not a clock time. */
export function clockLabel(time: string): string | null {
  const match = CLOCK.exec(time);
  if (!match) return null;
  const [h, m] = [Number(match[1]), Number(match[2])];
  if (h > 23 || m > 59) return null;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** "09:00", "12:00" -> "9 AM–12 PM"; "12:00", "17:00" -> "12–5 PM". Whole hours drop their ":00". */
export function hoursLabel(start: string, end: string): string | null {
  const from = CLOCK.exec(start);
  const to = CLOCK.exec(end);
  if (!from || !to) return null;
  const part = (h: number, m: number) => `${h % 12 === 0 ? 12 : h % 12}${m === 0 ? "" : `:${String(m).padStart(2, "0")}`}`;
  const [h1, m1, h2, m2] = [Number(from[1]), Number(from[2]), Number(to[1]), Number(to[2])];
  const meridiem = (h: number) => (h % 24 < 12 ? "AM" : "PM");
  return meridiem(h1) === meridiem(h2) ? `${part(h1, m1)}–${part(h2, m2)} ${meridiem(h2)}` : `${part(h1, m1)} ${meridiem(h1)}–${part(h2, m2)} ${meridiem(h2)}`;
}

/** "America/Los_Angeles" -> "Los Angeles". */
export function zoneCity(zoneId: string): string {
  const last = zoneId.split("/").pop() ?? zoneId;
  return last.replace(/_/g, " ");
}

/* ---------- zones ---------- */

function wallClockMs(ms: number, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
}

/** The zone's offset from UTC at an instant, in milliseconds. Throws a `RangeError` for a zone the runtime does not know. */
export function zoneOffsetMs(zone: string, instantMs: number): number {
  const whole = Math.floor(instantMs / 1000) * 1000;
  return wallClockMs(whole, zone) - whole;
}

/** The instant (UTC milliseconds) at which `date` `time` reads on the wall of `zone`. Null for a bad date, time or zone. */
export function wallClockToInstant(date: string, time: string, zone: string): number | null {
  if (!isIsoDate(date) || !CLOCK.test(time)) return null;
  try {
    const [y, m, d] = date.split("-").map(Number);
    const [h, min] = time.split(":").map(Number);
    const naive = Date.UTC(y, m - 1, d, h, min);
    // Two passes settle the offset when the wall time is near a daylight-saving change.
    const first = naive - zoneOffsetMs(zone, naive);
    return naive - zoneOffsetMs(zone, first);
  } catch {
    return null;
  }
}

/** The wall-clock day and time of an instant in `zone`. Null for a zone the runtime does not know. */
export function instantToWall(instantMs: number, zone: string): { date: string; time: string } | null {
  try {
    const wall = new Date(wallClockMs(instantMs, zone));
    const pad = (value: number) => String(value).padStart(2, "0");
    return { date: `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}`, time: `${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}` };
  } catch {
    return null;
  }
}

/** The day and time in `to` for a wall-clock `date` `time` in `from`. Null when either zone is unknown. */
export function convertWallTime(input: { date: string; time: string; from: string; to: string }): { date: string; time: string } | null {
  const instant = wallClockToInstant(input.date, input.time, input.from);
  return instant === null ? null : instantToWall(instant, input.to);
}

/** How the visitor's clock compares: "3:30 PM your time" (and "(Sat, Nov 7)" when it is another day there), or null when it is the same wall time. */
export function visitorTimeLine(input: { date: string; time: string; orgZone: string; visitorZone: string }): string | null {
  if (input.orgZone === input.visitorZone) return null;
  const there = convertWallTime({ date: input.date, time: input.time, from: input.orgZone, to: input.visitorZone });
  if (!there) return null;
  const label = clockLabel(there.time);
  if (!label) return null;
  if (there.date === input.date && there.time === input.time) return null;
  return there.date === input.date ? `${label} your time` : `${label} your time (${dayLabelOf(there.date)})`;
}

/** "1 h", "13 h 30 min" or "30 min" the visitor's clock is ahead of (+) or behind (-) the organization's at that moment; null when the same or unknown. */
export function zoneDifferenceLabel(input: { orgZone: string; visitorZone: string; atMs?: number }): string | null {
  if (input.orgZone === input.visitorZone) return null;
  try {
    const at = input.atMs ?? Date.now();
    const minutes = Math.round((zoneOffsetMs(input.visitorZone, at) - zoneOffsetMs(input.orgZone, at)) / 60000);
    if (minutes === 0) return null;
    const abs = Math.abs(minutes);
    const [hours, rest] = [Math.floor(abs / 60), abs % 60];
    const amount = rest === 0 ? `${hours} h` : hours === 0 ? `${rest} min` : `${hours} h ${rest} min`;
    return `${amount} ${minutes > 0 ? "ahead" : "behind"}`;
  } catch {
    return null;
  }
}

/* ---------- the visitor page's `?do=` ---------- */

/** What an emailed button asks for on the tour page. It only preselects: the visitor still clicks to send it. */
export type TourIntent = "accept_proposal" | "coming" | "cant_make_it" | "cancel" | "pick";

const INTENTS: Readonly<Record<string, TourIntent>> = {
  accept: "accept_proposal",
  accept_proposal: "accept_proposal",
  coming: "coming",
  cant: "cant_make_it",
  cant_make_it: "cant_make_it",
  cancel: "cancel",
  pick: "pick",
};

/** The `?do=` value as an intent; anything else (including a made-up name) is no intent at all. */
export function parseTourIntent(value: string | null | undefined): TourIntent | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(INTENTS, key) ? INTENTS[key] : null;
}
